import type { ReportDataSettingsV2 } from "@/common/report-configuration.ts";

import { FIELD_BASES, FieldBase, FieldName } from "@/common/fields.ts";

type DimensionData = {
  dimension: DimensionName;
  isTimeDimension: boolean;
  description: string;
  buckets?: number[];
};

export type DimensionV2 = FieldBase & DimensionData;

export type DimensionName = Exclude<
  FieldName,
  | "ANNUALIZED_TOTAL_CASH_COMPENSATION"
  | "APPLICATION_ID"
  | "APPLICANT_COUNT"
  | "ATTRITION_DRIVERS_IMPORTANCE"
  | "ATTRITION_PROBABILITY"
  | "ATTRITION_PROBABILITY_TIME_HORIZON"
  | "BELONGING"
  | "BONUS_AMOUNT"
  | "COMMISSION_AMOUNT"
  | "COMPANY_SIZE"
  | "DATE_OF_PROMOTION"
  | "EMPLOYEE_COUNT"
  | "ENGAGEMENT"
  | "HIRE_DATE"
  | "INDUSTRY"
  | "INDUSTRY_CONTEXT_INDUSTRY_POSITIONS_CHANGE_YOY"
  | "INDUSTRY_CONTEXT_INDUSTRY_SHARE_OF_OCCUPATION"
  | "INDUSTRY_CONTEXT_OCCUPATION_TOTAL_POSITIONS"
  | "INDUSTRY_SEPARATION_INTENSITY"
  | "INDUSTRY_TOTAL_POSITIONS"
  | "INSIGHTS_CONSCIOUS_BLUE_ENERGY"
  | "INSIGHTS_CONSCIOUS_RED_ENERGY"
  | "INSIGHTS_CONSCIOUS_GREEN_ENERGY"
  | "INSIGHTS_CONSCIOUS_YELLOW_ENERGY"
  | "INSIGHTS_LESS_CONSCIOUS_BLUE_ENERGY"
  | "INSIGHTS_LESS_CONSCIOUS_RED_ENERGY"
  | "INSIGHTS_LESS_CONSCIOUS_GREEN_ENERGY"
  | "INSIGHTS_LESS_CONSCIOUS_YELLOW_ENERGY"
  | "JOB_REQ_ACCEPTANCE_RATE"
  | "LABOR_AVAILABILITY_CURRENT_LABOR_AVAILABILITY_RATIO"
  | "LABOR_AVAILABILITY_EMPLOYED_WORKERS"
  | "LABOR_AVAILABILITY_LABOR_AVAILABILITY_CHANGE_FROM_12M_AVERAGE"
  | "LABOR_AVAILABILITY_LOCAL_MINUS_NATIONAL_LABOR_AVAILABILITY"
  | "LABOR_AVAILABILITY_NATIONAL_CURRENT_LABOR_AVAILABILITY_RATIO"
  | "LABOR_AVAILABILITY_TRAILING_12M_EMPLOYED_WORKERS"
  | "LABOR_AVAILABILITY_TRAILING_12M_LABOR_AVAILABILITY_RATIO"
  | "LABOR_AVAILABILITY_TRAILING_12M_UNEMPLOYED_WORKERS"
  | "LABOR_AVAILABILITY_UNEMPLOYED_WORKERS"
  | "LAG_TIME_DAYS"
  | "MARKET_PRESSURE_ROLE"
  | "MARKET_PRESSURE_MARKET"
  | "OCCUPATION_MARKET_EMP_CHANGE_YOY"
  | "OCCUPATION_MARKET_GROWTH_OPENINGS"
  | "OCCUPATION_MARKET_HIRES"
  | "OCCUPATION_MARKET_LOCAL_EARNINGS_PREMIUM"
  | "OCCUPATION_MARKET_REPLACEMENT_OPENING_RATE"
  | "OCCUPATION_MARKET_REPLACEMENT_OPENINGS"
  | "OCCUPATION_MARKET_RESIDENT_EMPLOYMENT"
  | "OCCUPATION_MARKET_SEPARATIONS"
  | "OCCUPATION_MARKET_YOY_HIRES_CHANGE"
  | "OCCUPATION_MARKET_YOY_REPLACEMENT_OPENINGS_CHANGE"
  | "OCCUPATION_MARKET_YOY_SEPARATIONS_CHANGE"
  | "OCCUPATION_LOCAL_HIRE_RATE"
  | "OCCUPATION_NATIONAL_HIRE_RATE"
  | "OCCUPATION_LOCAL_MINUS_NATIONAL_HIRE_RATE"
  | "OCCUPATION_NET_EMPLOYMENT_GROWTH_RATE"
  | "OCCUPATION_SEPARATION_INTENSITY"
  | "OCCUPATION_OCCUPIED_POSITIONS"
  | "POSITIONS_IN_INDUSTRY_AND_OCCUPATION"
  | "RACE_GROUPING"
  | "SATISFACTION"
  | "SECTOR_UNEMPLOYMENT_SHARE_OF_AREA_UNEMPLOYMENT"
  | "SECTOR_UNEMPLOYMENT_TRAILING_12M_UNEMPLOYED_WORKERS_AVERAGE"
  | "SECTOR_UNEMPLOYMENT_UNEMPLOYED_CHANGE_YOY"
  | "SECTOR_UNEMPLOYMENT_UNEMPLOYED_WORKERS"
  | "SURVEY_STATUS"
  | "TERMINATION_DATE"
  | "TIME_TO_FILL_DAYS"
  | "TIME_TO_FILL_DRIVERS_IMPACT"
  | "TIME_TO_HIRE_DAYS"
  | "TOTAL_COMPENSATION"
>;

export const DIMENSIONS: Record<DimensionName, DimensionV2> = {
  ACTION_TAKEN: {
    ...FIELD_BASES.ACTION_TAKEN,
    dimension: "ACTION_TAKEN",
    isTimeDimension: false,
    description: "The action taken in response to an infraction.",
  },
  ACTIVITY_DATE: {
    ...FIELD_BASES.ACTIVITY_DATE,
    dimension: "ACTIVITY_DATE",
    isTimeDimension: true,
    description: "The date when an infraction occurred.",
  },
  AGE_GROUP: {
    ...FIELD_BASES.AGE_GROUP,
    dimension: "AGE_GROUP",
    isTimeDimension: false,
    description:
      "Groups employees by age range (e.g., '18-24', '25-34', '35-44'). Use for analyzing workforce demographics and age-related trends.",
  },
  ANSWER_VALUE: {
    ...FIELD_BASES.ANSWER_VALUE,
    dimension: "ANSWER_VALUE",
    isTimeDimension: false,
    description:
      "The value of survey answers on a Likert scale. Use for analyzing employee sentiment and experience based on survey responses.",
  },
  APPLICANT_SOURCE: {
    ...FIELD_BASES.APPLICANT_SOURCE,
    dimension: "APPLICANT_SOURCE",
    isTimeDimension: false,
    description:
      "The high-level source from which applicants applied (e.g., 'Job Sites', 'Internal', etc). Use SOURCE_DETAIL for more granular analysis of applicant origins.",
  },
  APPLICANT_SOURCE_DETAIL: {
    ...FIELD_BASES.APPLICANT_SOURCE_DETAIL,
    dimension: "APPLICANT_SOURCE_DETAIL",
    isTimeDimension: false,
    description:
      "The detailed source from which applicants applied (e.g., 'LinkedIn', 'Indeed', etc). Use for granular analysis of applicant origins.",
  },
  APPLICATION_DATE: {
    ...FIELD_BASES.APPLICATION_DATE,
    dimension: "APPLICATION_DATE",
    isTimeDimension: true,
    description:
      "The date when job applications were submitted. Use for tracking recruitment trends and application volumes over time.",
  },
  BONUS_REASON: {
    ...FIELD_BASES.BONUS_REASON,
    dimension: "BONUS_REASON",
    isTimeDimension: false,
    description:
      "The reason for awarding a bonus. Use for analyzing bonus distribution and reasons across the workforce.",
  },
  COMMISSION_REASON: {
    ...FIELD_BASES.COMMISSION_REASON,
    dimension: "COMMISSION_REASON",
    isTimeDimension: false,
    description:
      "The type of or reason for awarding a commission. Use for analyzing commission distribution and reasons across the workforce.",
  },
  COMPANY_ID: {
    ...FIELD_BASES.COMPANY_ID,
    dimension: "COMPANY_ID",
    isTimeDimension: false,
    description:
      "The unique identifier for the company, usually the company name. Users may also refer to this as a 'client'.",
  },
  COMPLETION_CATEGORY: {
    ...FIELD_BASES.COMPLETION_CATEGORY,
    dimension: "COMPLETION_CATEGORY",
    isTimeDimension: false,
    description:
      "Enum of completion states for employee goals. For example, 'Completed on time', 'Past due - incomplete', 'Abandoned', etc.",
  },
  COMPLETION_VELOCITY_DAYS: {
    ...FIELD_BASES.COMPLETION_VELOCITY_DAYS,
    dimension: "COMPLETION_VELOCITY_DAYS",
    isTimeDimension: false,
    description:
      "The number of days taken to complete an employee goal. Use for analyzing goal completion efficiency across the workforce.",
  },
  COUNTRY: {
    ...FIELD_BASES.COUNTRY,
    datasetName: ["main"], // Override to exclude "benchmark"
    dimension: "COUNTRY",
    isTimeDimension: false,
    description:
      "The country where employees are located. Use for geographic analysis and international workforce distribution.",
  },
  DATE: {
    ...FIELD_BASES.DATE,
    dimension: "DATE",
    isTimeDimension: true,
    description:
      "The primary time dimension for analyzing trends over time, stored in SQL format YYYY-MM-DD. Use whenever the user wants to track changes over months, quarters, or years.",
  },
  DEPARTMENT: {
    ...FIELD_BASES.DEPARTMENT,
    dimension: "DEPARTMENT",
    isTimeDimension: false,
    description:
      "The department or functional area where employees work. Use for organizational analysis and department-specific metrics.",
  },
  DISABILITY_STATUS: {
    ...FIELD_BASES.DISABILITY_STATUS,
    dimension: "DISABILITY_STATUS",
    isTimeDimension: false,
    description:
      "Indicates whether employees have disclosed a disability status. Use for diversity and inclusion reporting.",
  },
  DISCIPLINARY_STATUS: {
    ...FIELD_BASES.DISCIPLINARY_STATUS,
    dimension: "DISCIPLINARY_STATUS",
    isTimeDimension: false,
    description:
      "Value indicating the status of a disciplinary case (e.g., 'Open', 'Closed').",
  },
  DISCUSSION_DATE: {
    ...FIELD_BASES.DISCUSSION_DATE,
    dimension: "DISCUSSION_DATE",
    isTimeDimension: true,
    description:
      "The date when a disciplinary discussion and action took place.",
  },
  DIVISION: {
    ...FIELD_BASES.DIVISION,
    dimension: "DIVISION",
    isTimeDimension: false,
    description:
      "The high-level business division or business unit. Use for organizational hierarchy analysis, typically broader than departments.",
  },
  EDUCATION: {
    ...FIELD_BASES.EDUCATION,
    dimension: "EDUCATION",
    isTimeDimension: false,
    description:
      "The highest level of education completed by employees (e.g., 'Bachelor's', 'Master's', 'PhD'). Use for workforce qualification analysis.",
  },
  EMPLOYEE_COUNTY_NAME: {
    ...FIELD_BASES.EMPLOYEE_COUNTY_NAME,
    dimension: "EMPLOYEE_COUNTY_NAME",
    isTimeDimension: false,
    description:
      "County of the employee's HOME ZIP. Not the worksite, and not necessarily where the market numbers came from.",
  },
  EMPLOYEE_GOAL_COMPLETION_PERCENT: {
    ...FIELD_BASES.EMPLOYEE_GOAL_COMPLETION_PERCENT,
    dimension: "EMPLOYEE_GOAL_COMPLETION_PERCENT",
    isTimeDimension: false,
    description:
      "The percentage of an employee goal that has been completed. Use for tracking progress and performance against set goals.",
  },
  EMPLOYEE_GOAL_CREATED_DATE: {
    ...FIELD_BASES.EMPLOYEE_GOAL_CREATED_DATE,
    dimension: "EMPLOYEE_GOAL_CREATED_DATE",
    isTimeDimension: true,
    description:
      "The date when an employee goal was created. Use for tracking goal-setting trends and employee engagement over time.",
  },
  EMPLOYEE_GOAL_COMPLETED_DATE: {
    ...FIELD_BASES.EMPLOYEE_GOAL_COMPLETED_DATE,
    dimension: "EMPLOYEE_GOAL_COMPLETED_DATE",
    isTimeDimension: true,
    description:
      "The date when an employee goal was completed. Use for analyzing goal achievement rates and performance trends.",
  },
  EMPLOYEE_GOAL_DUE_DATE: {
    ...FIELD_BASES.EMPLOYEE_GOAL_DUE_DATE,
    dimension: "EMPLOYEE_GOAL_DUE_DATE",
    isTimeDimension: true,
    description:
      "The due date for an employee goal. Use for monitoring upcoming deadlines and goal completion timelines.",
  },
  EMPLOYEE_GOAL_TYPE: {
    ...FIELD_BASES.EMPLOYEE_GOAL_TYPE,
    dimension: "EMPLOYEE_GOAL_TYPE",
    isTimeDimension: false,
    description:
      "The type or category of an employee goal (e.g., 'Individual', 'Cascading'). Use for analyzing goal types and their impact on employee performance.",
  },
  EMPLOYEE_GOAL_STATUS: {
    ...FIELD_BASES.EMPLOYEE_GOAL_STATUS,
    dimension: "EMPLOYEE_GOAL_STATUS",
    isTimeDimension: false,
    description:
      "The current status of an employee goal (e.g., 'Completed', 'In Progress'). Use for tracking goal progress and completion rates.",
  },
  EMPLOYEE_MSA_NAME: {
    ...FIELD_BASES.EMPLOYEE_MSA_NAME,
    dimension: "EMPLOYEE_MSA_NAME",
    isTimeDimension: false,
    description:
      "The Metropolitan Statistical Area (MSA) name for the location of the employee's HOME ZIP. Use for geographic and industry-specific analysis.",
  },
  EMPLOYMENT_TYPE: {
    ...FIELD_BASES.EMPLOYMENT_TYPE,
    dimension: "EMPLOYMENT_TYPE",
    isTimeDimension: false,
    description:
      "The type of employment (e.g., 'Full-time', 'Part-time', 'Contractor'). Use for analyzing workforce composition and employment trends.",
  },
  ETHNICITY: {
    ...FIELD_BASES.ETHNICITY,
    dimension: "ETHNICITY",
    isTimeDimension: false,
    description:
      "Employee ethnicity categories for diversity tracking. Use for detailed ethnic diversity analysis and DEI reporting.",
  },
  EXPECTED_START_DATE: {
    ...FIELD_BASES.EXPECTED_START_DATE,
    dimension: "EXPECTED_START_DATE",
    isTimeDimension: true,
    description:
      "The expected start date for new hires. Use for tracking upcoming workforce changes and planning onboarding activities.",
  },
  GENDER: {
    ...FIELD_BASES.GENDER,
    dimension: "GENDER",
    isTimeDimension: false,
    description:
      "Employee gender identity (e.g., 'Male', 'Female', 'Non-binary'). Use for gender diversity analysis and pay equity studies.",
  },
  GENERATION: {
    ...FIELD_BASES.GENERATION,
    dimension: "GENERATION",
    isTimeDimension: false,
    description:
      "Generational cohorts based on birth year (e.g., 'Gen Z', 'Millennial', 'Gen X', 'Baby Boomer'). Use for generational workforce analysis and trends.",
  },
  HIRED_DATE: {
    ...FIELD_BASES.HIRED_DATE,
    dimension: "HIRED_DATE",
    isTimeDimension: true,
    description:
      "The date when employees were hired. Use for tracking hiring trends, tenure analysis, and workforce growth over time.",
  },
  HIRING_MANAGER: {
    ...FIELD_BASES.HIRING_MANAGER,
    dimension: "HIRING_MANAGER",
    isTimeDimension: false,
    description:
      "The main hiring manager responsible for the recruitment process. Use for analyzing hiring patterns and manager-specific recruitment performance.",
  },
  INFRACTION_DESCRIPTION: {
    ...FIELD_BASES.INFRACTION_DESCRIPTION,
    dimension: "INFRACTION_DESCRIPTION",
    isTimeDimension: false,
    description:
      "The description of the infraction that occurred. Use for analyzing the nature and details of disciplinary cases.",
  },
  INSIGHTS_8_TYPE: {
    ...FIELD_BASES.INSIGHTS_8_TYPE,
    dimension: "INSIGHTS_8_TYPE",
    isTimeDimension: false,
    description:
      "The most salient core Insights Discovery type (persona) for the employee.",
  },
  INSIGHTS_OPPOSITE_8_TYPE: {
    ...FIELD_BASES.INSIGHTS_OPPOSITE_8_TYPE,
    dimension: "INSIGHTS_OPPOSITE_8_TYPE",
    isTimeDimension: false,
    description:
      "The core Insights Discovery type (persona) that is opposite the primary type for the employee.",
  },
  INSIGHTS_16_TYPE: {
    ...FIELD_BASES.INSIGHTS_16_TYPE,
    dimension: "INSIGHTS_16_TYPE",
    isTimeDimension: false,
    description:
      "The detailed Insights Discovery type (persona) for the employee, providing a more granular view of their behavioral preferences based on the assessment.",
  },
  INSIGHTS_72_TYPE: {
    ...FIELD_BASES.INSIGHTS_72_TYPE,
    dimension: "INSIGHTS_72_TYPE",
    isTimeDimension: false,
    description:
      "The position on the 72-position wheel based on the person’s conscious persona.",
  },
  INSIGHTS_LESS_CONSCIOUS_72_TYPE: {
    ...FIELD_BASES.INSIGHTS_LESS_CONSCIOUS_72_TYPE,
    dimension: "INSIGHTS_LESS_CONSCIOUS_72_TYPE",
    isTimeDimension: false,
    description:
      "The position on the 72-position wheel based on the person’s less conscious persona.",
  },
  INSIGHTS_LEADING_CONSCIOUS_COLOR: {
    ...FIELD_BASES.INSIGHTS_LEADING_CONSCIOUS_COLOR,
    dimension: "INSIGHTS_LEADING_CONSCIOUS_COLOR",
    isTimeDimension: false,
    description:
      "The color with the highest score from the Insights Discovery assessment for the employee.",
  },
  INSIGHTS_SUPERVISOR_SAME_8_TYPE: {
    ...FIELD_BASES.INSIGHTS_SUPERVISOR_SAME_8_TYPE,
    dimension: "INSIGHTS_SUPERVISOR_SAME_8_TYPE",
    isTimeDimension: false,
    description:
      "Whether the Insights Discovery core type (persona) for the employee's supervisor is the same as the employee's primary type.",
  },
  INSIGHTS_SUPERVISOR_OPPOSITE_8_TYPE: {
    ...FIELD_BASES.INSIGHTS_SUPERVISOR_OPPOSITE_8_TYPE,
    dimension: "INSIGHTS_SUPERVISOR_OPPOSITE_8_TYPE",
    isTimeDimension: false,
    description:
      "Whether the Insights Discovery core type (persona) for the employee's supervisor is opposite the employee's primary type.",
  },
  JOB_BAND: {
    ...FIELD_BASES.JOB_BAND,
    dimension: "JOB_BAND",
    isTimeDimension: false,
    description:
      "The job band or level within the organization (e.g., 'Entry Level', 'Mid-Level', 'Senior'). Use for analyzing workforce structure and career progression.",
  },
  JOB_CATEGORY: {
    ...FIELD_BASES.JOB_CATEGORY,
    dimension: "JOB_CATEGORY",
    isTimeDimension: false,
    description:
      "The broad job category or classification (e.g., 'Call Center', 'Stores', 'Marketing'). Use for role-based analysis and workforce segmentation.",
  },
  JOB_FAMILY: {
    ...FIELD_BASES.JOB_FAMILY,
    dimension: "JOB_FAMILY",
    isTimeDimension: false,
    description:
      "The broader job family or category (e.g., 'Engineering', 'Marketing'). Use for role-based analysis, recruitment, and career path tracking.",
  },
  JOB_HIERARCHY: {
    ...FIELD_BASES.JOB_HIERARCHY,
    dimension: "JOB_HIERARCHY",
    isTimeDimension: false,
    description:
      "The job-level label used to describe a role's place in the organization, which may be a standard hierarchy level such as 'Entry Level' or 'Manager', or a more source-specific label such as 'Sales', 'Ast Mng', 'Non-Retail', or 'Mng'. Use for organizational structure and role-level analysis.",
  },
  JOB_REQ_FILL_DATE: {
    ...FIELD_BASES.JOB_REQ_FILL_DATE,
    dimension: "JOB_REQ_FILL_DATE",
    isTimeDimension: true,
    description:
      "The date when job requisitions were closed, whether or not a hire was made.",
  },
  JOB_REQ_ID: {
    ...FIELD_BASES.JOB_REQ_ID,
    dimension: "JOB_REQ_ID",
    isTimeDimension: false,
    description:
      "The unique identifier for job requisitions. Job reqs may share the same JOB_TITLE but have different JOB_REQ_IDs.",
  },
  JOB_REQ_OPEN_DATE: {
    ...FIELD_BASES.JOB_REQ_OPEN_DATE,
    dimension: "JOB_REQ_OPEN_DATE",
    isTimeDimension: true,
    description:
      "The date when job requisitions were opened. Use for tracking recruitment trends and job req volumes over time.",
  },
  JOB_REQ_STATUS: {
    ...FIELD_BASES.JOB_REQ_STATUS,
    dimension: "JOB_REQ_STATUS",
    isTimeDimension: false,
    description:
      "The current status of job requisitions (e.g., 'Open', 'Closed', 'Filled'). Use for recruitment pipeline analysis and job req tracking.",
  },
  JOB_TITLE: {
    ...FIELD_BASES.JOB_TITLE,
    dimension: "JOB_TITLE",
    isTimeDimension: false,
    description:
      "The specific job title or role of employees. Use for role-specific analysis and career path tracking. Note: may have many unique values.",
  },
  LAG_TIME_BUCKET: {
    ...FIELD_BASES.LAG_TIME_BUCKET,
    dimension: "LAG_TIME_BUCKET",
    isTimeDimension: false,
    description:
      "The bucketed range of lag time, being the elapsed time between OSHA incident date and when the employer was notified, in days.",
  },
  LGBTQ_STATUS: {
    ...FIELD_BASES.LGBTQ_STATUS,
    dimension: "LGBTQ_STATUS",
    isTimeDimension: false,
    description:
      "Indicates LGBTQ+ status disclosure. Use for LGBTQ+ diversity and inclusion reporting.",
  },
  LOCATION: {
    ...FIELD_BASES.LOCATION,
    dimension: "LOCATION",
    isTimeDimension: false,
    description:
      "Values are one of 'Office', 'Remote' or 'Hybrid'. Not location in the geographic sense.",
  },
  NAICS_NAME: {
    ...FIELD_BASES.NAICS_NAME,
    dimension: "NAICS_NAME",
    isTimeDimension: false,
    description:
      "The North American Industry Classification System (NAICS) name for the industry. Use for industry-specific analysis and benchmarking.",
  },
  OSHA_INCIDENT_DATE: {
    ...FIELD_BASES.OSHA_INCIDENT_DATE,
    dimension: "OSHA_INCIDENT_DATE",
    isTimeDimension: true,
    description:
      "The date when an OSHA incident occurred. Use for tracking incident trends and compliance over time.",
  },
  OSHA_INCIDENT_TYPE: {
    ...FIELD_BASES.OSHA_INCIDENT_TYPE,
    dimension: "OSHA_INCIDENT_TYPE",
    isTimeDimension: false,
    description:
      "The type of OSHA incident (e.g., 'Injury', 'Illness', 'Near Miss'). Use for analyzing incident types and safety trends.",
  },
  OSHA_AFFECTED_BODY_PART: {
    ...FIELD_BASES.OSHA_AFFECTED_BODY_PART,
    dimension: "OSHA_AFFECTED_BODY_PART",
    isTimeDimension: false,
    description:
      "The body part affected by the OSHA incident (e.g., 'Hand', 'Back', 'Eye'). Use for analyzing injury patterns and safety measures.",
  },
  OSHA_FIRST_AID_ONLY: {
    ...FIELD_BASES.OSHA_FIRST_AID_ONLY,
    dimension: "OSHA_FIRST_AID_ONLY",
    isTimeDimension: false,
    description:
      "Indicates whether the OSHA incident required only first aid treatment (Y/N). Use for analyzing incident severity and safety compliance.",
  },
  OSHA_ON_COMPANY_PROPERTY: {
    ...FIELD_BASES.OSHA_ON_COMPANY_PROPERTY,
    dimension: "OSHA_ON_COMPANY_PROPERTY",
    isTimeDimension: false,
    description:
      "Indicates whether the OSHA incident occurred on company property (Y/N). Use for analyzing workplace safety and incident locations.",
  },
  OSHA_OCCUPATION: {
    ...FIELD_BASES.OSHA_OCCUPATION,
    dimension: "OSHA_OCCUPATION",
    isTimeDimension: false,
    description:
      "The occupation or job role of the employee involved in the OSHA incident. Use for analyzing incident patterns across different job roles.",
  },
  OSHA_ESTABLISHMENT_NAME: {
    ...FIELD_BASES.OSHA_ESTABLISHMENT_NAME,
    dimension: "OSHA_ESTABLISHMENT_NAME",
    isTimeDimension: false,
    description:
      "The name of the establishment or facility where the OSHA incident occurred. Use for analyzing incident trends across different locations.",
  },
  ORIGIN: {
    ...FIELD_BASES.ORIGIN,
    dimension: "ORIGIN",
    isTimeDimension: false,
    description: "The applicant origin, external or internal.",
  },
  PAY_BAND: {
    ...FIELD_BASES.PAY_BAND,
    dimension: "PAY_BAND",
    isTimeDimension: false,
    description:
      "Salary range or compensation band (e.g., 'Band 1', 'Band 2'). Use for compensation analysis and pay equity studies.",
  },
  PERFORMANCE_RATING: {
    ...FIELD_BASES.PERFORMANCE_RATING,
    dimension: "PERFORMANCE_RATING",
    isTimeDimension: false,
    description:
      "Employee performance ratings on a 0-5 scale.Use for performance analysis and talent management.",
    buckets: [0, 1, 2, 3, 4, 5],
  },
  PRIMARY_RECRUITER: {
    ...FIELD_BASES.PRIMARY_RECRUITER,
    dimension: "PRIMARY_RECRUITER",
    isTimeDimension: false,
    description:
      "The main recruiter responsible for hiring. Use for recruitment performance analysis and recruiter workload tracking.",
  },
  PRIMARY_WORK_LOCATION: {
    ...FIELD_BASES.PRIMARY_WORK_LOCATION,
    dimension: "PRIMARY_WORK_LOCATION",
    isTimeDimension: false,
    description:
      "The primary work location of employees, such as 'New York Office', 'Seattle Office', or 'Remote'. Use for analyzing workforce distribution across specific locations.",
  },
  QUESTION_LABEL: {
    ...FIELD_BASES.QUESTION_LABEL,
    dimension: "QUESTION_LABEL",
    isTimeDimension: false,
    description:
      "The survey question asked (e.g., 'I feel valued at work'). Use for analyzing employee sentiment and experience based on survey responses.",
  },
  QUESTION_SUBTHEME: {
    ...FIELD_BASES.QUESTION_SUBTHEME,
    dimension: "QUESTION_SUBTHEME",
    isTimeDimension: false,
    description:
      "The subtheme or more specific category of survey questions. Use for analyzing employee sentiment and experience based on survey responses.",
  },
  QUESTION_THEME: {
    ...FIELD_BASES.QUESTION_THEME,
    dimension: "QUESTION_THEME",
    isTimeDimension: false,
    description:
      "The theme or category of survey questions (e.g., 'Pay and Benefits', 'Leadership'). Use for analyzing employee sentiment and experience based on survey responses.",
  },
  RACE: {
    ...FIELD_BASES.RACE,
    dimension: "RACE",
    isTimeDimension: false,
    description:
      "Employee race categories. Use for racial diversity analysis and DEI reporting. Available in benchmark data.",
  },
  RECRUITMENT_STAGE: {
    ...FIELD_BASES.RECRUITMENT_STAGE,
    dimension: "RECRUITMENT_STAGE",
    isTimeDimension: false,
    description:
      "The current stage of the recruitment process for applicants (for example, 'Applied', 'Interview', 'Hired'). Use for recruitment funnel analysis.",
  },
  RECRUITMENT_STAGE_HISTORICAL: {
    ...FIELD_BASES.RECRUITMENT_STAGE_HISTORICAL,
    dimension: "RECRUITMENT_STAGE_HISTORICAL",
    isTimeDimension: false,
    description:
      "The historical stage of the recruitment process for applicants (for example, 'Applied', 'Interview', 'Hired'). Use for time-in-stage analysis and tracking applicant progress over time.",
  },
  REGION: {
    ...FIELD_BASES.REGION,
    dimension: "REGION",
    isTimeDimension: false,
    description:
      "Geographic region (e.g., 'Northeast', 'West Coast', 'EMEA'). Use for regional workforce analysis, broader than state but more specific than country.",
  },
  REGRETTABLE_ATTRITION: {
    ...FIELD_BASES.REGRETTABLE_ATTRITION,
    dimension: "REGRETTABLE_ATTRITION",
    isTimeDimension: false,
    description:
      "Categorizes attrition as 'Regrettable' (employees the company would prefer to keep) or 'Non-regrettable' (employees whose departure is less impactful). Use for analyzing attrition patterns and retention strategies.",
  },
  SOC_TITLE: {
    ...FIELD_BASES.SOC_TITLE,
    dimension: "SOC_TITLE",
    isTimeDimension: false,
    description:
      "The Standard Occupational Classification (SOC) title for the employee's role. Use for industry-specific workforce analysis and benchmarking.",
  },
  STATE: {
    ...FIELD_BASES.STATE,
    datasetName: ["main"], // Override to exclude "benchmark"
    dimension: "STATE",
    isTimeDimension: false,
    description:
      "US state or province where employees are located. Use for state-level geographic analysis and compliance reporting.",
  },
  STORE_NAME: {
    ...FIELD_BASES.STORE_NAME,
    dimension: "STORE_NAME",
    isTimeDimension: false,
    description:
      "The name or identifier of the store or retail location where employees work. Use for analyzing workforce distribution and performance across specific stores.",
  },
  STORE_STATUS: {
    ...FIELD_BASES.STORE_STATUS,
    dimension: "STORE_STATUS",
    isTimeDimension: false,
    description:
      "The operational status of the store (e.g., 'Open', 'Closed', 'Under Renovation'). Use for analyzing workforce allocation and performance based on store status.",
  },
  SUBMIT_DATE: {
    ...FIELD_BASES.SUBMIT_DATE,
    dimension: "SUBMIT_DATE",
    isTimeDimension: true,
    description:
      "The date when a survey response was submitted. Use for tracking submission trends and volumes over time.",
  },
  SUPERVISOR_ID: {
    ...FIELD_BASES.SUPERVISOR_ID,
    dimension: "SUPERVISOR_ID",
    isTimeDimension: false,
    description:
      "The unique identifier for employees' direct supervisors. Use for analyzing management structures and supervisor-specific workforce metrics.",
  },
  SUPERVISOR_JOB_TITLE: {
    ...FIELD_BASES.SUPERVISOR_JOB_TITLE,
    dimension: "SUPERVISOR_JOB_TITLE",
    isTimeDimension: false,
    description:
      "The job title of employees' direct supervisors. Use for analyzing management structures and supervisor-specific workforce metrics.",
  },
  SUPERVISOR_NAME: {
    ...FIELD_BASES.SUPERVISOR_NAME,
    dimension: "SUPERVISOR_NAME",
    isTimeDimension: false,
    description: "The name of the employees' direct supervisor.",
  },
  SURVEY_NAME: {
    ...FIELD_BASES.SURVEY_NAME,
    dimension: "SURVEY_NAME",
    isTimeDimension: false,
    description:
      "The name of the employee survey (e.g., '90 Day Onboarding Survey', 'Exit Survey'). Use for analyzing employee sentiment and experience based on survey responses.",
  },
  TENURE_GROUP: {
    ...FIELD_BASES.TENURE_GROUP,
    dimension: "TENURE_GROUP",
    isTimeDimension: false,
    description:
      "Groups employees by length of service (e.g., '0-1 years', '1-3 years', '3-5 years'). Use for retention analysis and tenure-related trends.",
  },
  TERMINATION_REASON: {
    ...FIELD_BASES.TERMINATION_REASON,
    dimension: "TERMINATION_REASON",
    isTimeDimension: false,
    description:
      "The specific reason for employee termination (e.g., 'Resignation', 'Retirement', 'Performance'). Use for detailed attrition analysis and exit pattern identification.",
  },
  TERMINATION_TYPE: {
    ...FIELD_BASES.TERMINATION_TYPE,
    dimension: "TERMINATION_TYPE",
    isTimeDimension: false,
    description:
      "Categorizes terminations as 'Voluntary' or 'Involuntary'. Use for high-level attrition analysis and retention strategy.",
  },
  VETERAN_STATUS: {
    ...FIELD_BASES.VETERAN_STATUS,
    dimension: "VETERAN_STATUS",
    isTimeDimension: false,
    description:
      "Indicates military veteran status. Use for veteran workforce diversity reporting and veteran hiring analysis.",
  },
  WORK_ARRANGEMENT_TYPE: {
    ...FIELD_BASES.WORK_ARRANGEMENT_TYPE,
    dimension: "WORK_ARRANGEMENT_TYPE",
    isTimeDimension: false,
    description:
      "The type of work arrangement (e.g., 'Remote', 'Office', 'Retail'). Use for analyzing workforce distribution and work arrangement trends.",
  },
};

export const findTimeDimensionV2 = (
  data: ReportDataSettingsV2,
): DimensionV2 | null => {
  const foundDimensionName = data.dimensions.find(
    (dim) => DIMENSIONS[dim]?.isTimeDimension,
  );
  if (foundDimensionName) {
    return DIMENSIONS[foundDimensionName];
  }
  return null;
};

export const addTimeDimensionByDataset = (config: {
  data: {
    datasetName: ReportDataSettingsV2["datasetName"];
    dimensions: DimensionName[];
  };
}) => {
  const newTimeDim = Object.values(DIMENSIONS).find(
    (dim) =>
      dim.isTimeDimension && dim.datasetName.includes(config.data.datasetName),
  );
  if (newTimeDim) {
    config.data.dimensions.push(newTimeDim.dimension as DimensionName);
  }
};
